import React, { useState, useRef } from "react";
import {
  Box,
  VStack,
  Heading,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  useToast,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  HStack,
  Image,
  Divider,
  Text,
  Link,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import axios from "axios";

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  associateId?: string;
}

interface PasswordRule {
  label: string;
  test: (password: string) => boolean;
  met: boolean;
}

const Signup = () => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    associateId: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  // Refs for focus management
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const associateIdRef = useRef<HTMLInputElement>(null);

  // Password validation rules
  const passwordRules: PasswordRule[] = [
    {
      label: "At least 8 characters",
      test: (pwd) => pwd.length >= 8,
      met: false,
    },
    {
      label: "One uppercase letter (A-Z)",
      test: (pwd) => /[A-Z]/.test(pwd),
      met: false,
    },
    {
      label: "One lowercase letter (a-z)",
      test: (pwd) => /[a-z]/.test(pwd),
      met: false,
    },
    {
      label: "One number (0-9)",
      test: (pwd) => /\d/.test(pwd),
      met: false,
    },
    {
      label: "One special character (@$!%*?&)",
      test: (pwd) => /[@$!%*?&]/.test(pwd),
      met: false,
    },
  ];

  // Update password rules based on current password
  const getPasswordRules = (): PasswordRule[] => {
    return passwordRules.map(rule => ({
      ...rule,
      met: rule.test(formData.password),
    }));
  };

  // Validation functions
  const validateName = (name: string): string | undefined => {
    if (!name.trim()) {
      return 'Name is required';
    }
    if (name.trim().length < 2) {
      return 'Name must be at least 2 characters';
    }
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password.trim()) {
      return 'Password is required';
    }
    
    const rules = getPasswordRules();
    const unmetRules = rules.filter(rule => !rule.met);
    
    if (unmetRules.length > 0) {
      return `Password must meet all requirements`;
    }
    
    return undefined;
  };

  const validateAssociateId = (associateId: string): string | undefined => {
    if (!associateId.trim()) {
      return 'Associate ID is required';
    }
    if (associateId.trim().length < 2) {
      return 'Associate ID must be at least 2 characters';
    }
    return undefined;
  };

  // Handle input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleBlur = (field: keyof FormErrors) => {
    const value = formData[field as keyof typeof formData];
    let error: string | undefined;

    switch (field) {
      case 'name':
        error = validateName(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'password':
        error = validatePassword(value);
        break;
      case 'associateId':
        error = validateAssociateId(value);
        break;
    }

    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    newErrors.name = validateName(formData.name);
    newErrors.email = validateEmail(formData.email);
    newErrors.password = validatePassword(formData.password);
    newErrors.associateId = validateAssociateId(formData.associateId);
    
    setErrors(newErrors);
    
    // Focus on first invalid field
    if (newErrors.name) {
      nameRef.current?.focus();
      return false;
    }
    if (newErrors.email) {
      emailRef.current?.focus();
      return false;
    }
    if (newErrors.password) {
      passwordRef.current?.focus();
      return false;
    }
    if (newErrors.associateId) {
      associateIdRef.current?.focus();
      return false;
    }
    
    return true;
  };

  // Handle signup
  const handleSignup = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const res = await axios.post(`${process.env.REACT_APP_API_URL}auth/signup`, { ...formData, role: "user" });

      setFormData({ name: "", email: "", password: "", associateId: "" });
      setErrors({});
      setSignupSuccess(true);

      // Show animation and redirect after delay
      setTimeout(() => {
        window.location.href = "/login";
      }, 2500);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const currentPasswordRules = getPasswordRules();

  return (
    <Box minH="100vh" bgGradient="linear(to-br, primary.50, white 60%)" display="flex" alignItems="center" justifyContent="center" p={4}>
      <Box maxW="400px" w="full" bg="white" borderRadius="2xl" boxShadow="2xl" p={8} borderWidth={1} borderColor="primary.100">
        <VStack spacing={6} align="stretch">
          {/* Logo & Title */}
          <VStack spacing={2} textAlign="center">
            <Image src="/favicon.png" alt="CMS Logo" boxSize="48px" mx="auto" />
            <Heading size="lg" color="primary.700">Client Management System</Heading>
            <Text color="gray.500" fontSize="md">Create your account to get started.</Text>
          </VStack>
          <Divider />



          {/* Signup Form or Success Animation */}
          {!signupSuccess ? (
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!errors.name}>
                <FormLabel>Name</FormLabel>
                <Input
                  id="name"
                  ref={nameRef}
                  name="name"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={() => handleBlur('name')}
                  size="lg"
                  bg="primary.50"
                  borderColor={errors.name ? 'red.500' : 'primary.200'}
                  _focus={{ 
                    borderColor: errors.name ? 'red.500' : 'primary.400', 
                    boxShadow: errors.name ? '0 0 0 2px #E53E3E' : '0 0 0 2px #7B4BC1' 
                  }}
                />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.associateId}>
                <FormLabel>Associate ID</FormLabel>
                <Input
                  id="associateId"
                  ref={associateIdRef}
                  name="associateId"
                  placeholder="Enter your associate ID"
                  value={formData.associateId}
                  onChange={handleChange}
                  onBlur={() => handleBlur('associateId')}
                  size="lg"
                  bg="primary.50"
                  borderColor={errors.associateId ? 'red.500' : 'primary.200'}
                  _focus={{ 
                    borderColor: errors.associateId ? 'red.500' : 'primary.400', 
                    boxShadow: errors.associateId ? '0 0 0 2px #E53E3E' : '0 0 0 2px #7B4BC1' 
                  }}
                />
                <FormErrorMessage>{errors.associateId}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.email}>
                <FormLabel>Email</FormLabel>
                <Input
                  id="email"
                  ref={emailRef}
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={() => handleBlur('email')}
                  size="lg"
                  bg="primary.50"
                  borderColor={errors.email ? 'red.500' : 'primary.200'}
                  _focus={{ 
                    borderColor: errors.email ? 'red.500' : 'primary.400', 
                    boxShadow: errors.email ? '0 0 0 2px #E53E3E' : '0 0 0 2px #7B4BC1' 
                  }}
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.password}>
                <FormLabel>Password</FormLabel>
                <InputGroup size="lg">
                  <Input
                    id="password"
                    ref={passwordRef}
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={() => handleBlur('password')}
                    bg="primary.50"
                    borderColor={errors.password ? 'red.500' : 'primary.200'}
                    _focus={{ 
                      borderColor: errors.password ? 'red.500' : 'primary.400', 
                      boxShadow: errors.password ? '0 0 0 2px #E53E3E' : '0 0 0 2px #7B4BC1' 
                    }}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label="Toggle password visibility"
                      size="sm"
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password}</FormErrorMessage>
              </FormControl>

              {/* Real-time Password Rules */}
              <Box bg="blue.50" p={4} borderRadius="md" border="1px" borderColor="blue.200">
                <Text fontSize="sm" color="blue.700" fontWeight="medium" mb={2}>
                  Password Requirements:
                </Text>
                <VStack spacing={1} align="start">
                  {currentPasswordRules.map((rule, index) => (
                    <HStack key={index} spacing={2}>
                      {rule.met ? (
                        <CheckIcon color="green.500" boxSize={3} />
                      ) : (
                        <CloseIcon color="red.500" boxSize={3} />
                      )}
                      <Text 
                        fontSize="xs" 
                        color={rule.met ? "green.600" : "red.600"}
                        fontWeight={rule.met ? "medium" : "normal"}
                      >
                        {rule.label}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>

              <Button
                colorScheme="primary"
                width="full"
                onClick={handleSignup}
                isLoading={loading}
                size="lg"
                borderRadius="xl"
                fontWeight="bold"
                boxShadow="md"
              >
                Sign Up
              </Button>

              <Text fontSize="sm" textAlign="center">
                Already have an account?{' '}
                <Link color="primary.500" fontWeight="bold" href="/login">Login</Link>
              </Text>
            </VStack>
          ) : (
            <VStack spacing={6} align="center" justify="center" py={10}>
              <Image src="/favicon.png" alt="Success" boxSize="56px" mx="auto" />
              <Heading size="md" color="primary.700">Signup Successful!</Heading>
              <Text color="primary.500" fontSize="lg">Redirecting to login page...</Text>
              {/* Simple animation */}
              <Box w="100%" display="flex" justifyContent="center">
                <Box as="span" className="loader" w="40px" h="40px" borderRadius="full" borderWidth="4px" borderColor="primary.300" borderTopColor="primary.500" animation="spin 1s linear infinite" />
              </Box>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                .loader {
                  display: inline-block;
                  border-style: solid;
                  border-width: 4px;
                  border-radius: 50%;
                  border-color: #D6BCFA;
                  border-top-color: #7B4BC1;
                  width: 40px;
                  height: 40px;
                  animation: spin 1s linear infinite;
                }
              `}</style>
            </VStack>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default Signup;
